// Every user-facing string lives here (PLAN D10). The UI is English — the domain vocabulary
// already is — but keeping the strings in one module means that is a data decision, not
// something welded into JSX.
export const t = {
  appName: "Flight Radar",
  tagline: "What's flying over you, and what's about to be.",
  loading: "Loading…",

  common: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    working: "Working…",
  },

  nav: { radar: "Radar", alerts: "Alerts", settings: "Settings" },

  login: {
    title: "Sign in",
    username: "Username",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    failed: "Invalid username or password.",
    unreachable: "Can't reach the server. Check that the backend is running.",
  },

  radar: {
    signOut: "Sign out",
    locate: "Centre on me",
    locating: "Finding you…",
    locationDenied: "Location unavailable — showing the default area.",
    connecting: "Connecting…",
    live: (n: number) => `${n} aircraft`,
    offline: "Reconnecting…",
    empty: "No aircraft in view",
    loadingArea: "Loading area…",
    finding: (label: string) => `Finding ${label}…`,
    notTracked: (label: string) => `${label} is no longer being tracked.`,
    alertsAsleep: "Alerts are asleep",
    alertsAsleepUntil: (time: string) => `Alerts asleep until ${time}`,
    liveFiltered: (shown: number, total: number) => `${shown} of ${total} aircraft`,
    sizeFilter: { 0: "All sizes", 2: "Airliners+", 3: "Wide-body", 4: "4-engine" } as Record<number, string>,
    sizeFilterAria: (current: string) => `Size filter: ${current}. Tap to change.`,
  },

  detail: {
    close: "Close",
    unknownType: "Unknown type",
    distance: "Distance",
    altitude: "Altitude",
    speed: "Ground speed",
    vertical: "Vertical",
    track: "Track",
    squawk: "Squawk",
    hex: "ICAO",
    level: "Level",
    onGround: "On ground",
    emergency: "Emergency",
    muteType: (type: string) => `Mute ${type}`,
    unmuteType: (type: string) => `Unmute ${type}`,
    mutedNote: "Muted — this type still shows on the map, it just won't notify you.",
    photoAlt: "Photograph of this aircraft",
    fromHome: "from home",
    outlook: (rule: string, entryS: number, blockedBy: string | null) => {
      const when = entryS <= 20 ? "now" : entryS < 90 ? `in ${Math.round(entryS / 10) * 10}s` : `in ${Math.round(entryS / 60)} min`;
      const base = `Enters “${rule}” ${when}`;
      if (!blockedBy) return `${base} — you'll be alerted`;
      if (blockedBy === "muted") return `${base} — muted type, no alert`;
      if (blockedBy === "night") return `${base} — asleep until sunrise, no alert`;
      return `${base} — ${blockedBy}, no alert`;
    },
    outlookMore: (n: number) => `(+${n} more)`,
    sizeLabel: {
      light: "Light",
      rotorcraft: "Helicopter",
      small: "Small",
      large: "Airliner",
      heavy: "Wide-body",
      four_engine: "Four-engine",
    } as Record<string, string>,
    etaPrefix: "ETA ~",
    toGo: "to go",
    routeUnverified: "route unconfirmed",
    forecastShort: (minutes: number) => `Dashed: next ${minutes} min`,
    noForecastShort: "No forecast (no speed/track)",
    mutedShort: "Muted",
    remembered: "from memory",
    rememberedHint:
      "This frame carried no model name; shown from the type dictionary learned earlier.",
    forecast: (minutes: number) => `Dashed line: next ${minutes} minutes if it holds course.`,
    forecastLoading: "Working out where it's going…",
    noForecast: "No forecast — this aircraft isn't reporting speed or track.",
  },

  homePrompt: {
    title: "Save a home point?",
    body: "Alerts need somewhere to watch. Saving your location lets them work even when the app is closed.",
    consequence:
      "You can skip this. Alerts will then only work while the app is open, since a closed app can't report where you are.",
    use: "Use my location",
    dismiss: "Not now",
    failed: "Couldn't get your location.",
  },

  types: {
    title: "Aircraft types",
    link: "Aircraft types",
    intro:
      "Alerts are about which aeroplane it is, not who operates it. Silence the types you've seen enough of; the rare ones keep coming through.",
    suggestionsTitle: "Seen a lot lately",
    suggestionsWhy: "These have passed through your zones often enough that they may not be news any more.",
    mutedTitle: "Muted",
    historyTitle: "Everything you've seen",
    noHistory: "Nothing yet. Types appear here once an alert zone has caught something.",
    unknown: "Unknown type",
    automatic: "automatic",
    mute: "Mute",
    unmute: "Unmute",
    seen: (n: number) => `${n} ${n === 1 ? "time" : "times"}`,
    seenAndNotified: (seen: number, notified: number) =>
      notified === seen
        ? `${seen} ${seen === 1 ? "time" : "times"}`
        : `${seen} times · ${notified} notified`,
  },

  alerts: {
    title: "Alerts",
    empty: "No alerts yet. Create one to be told before an aircraft passes over you.",
    create: "New alert",
    name: "Name",
    anchor: "Watch around",
    anchorAuto: "Me, falling back to home",
    anchorHome: "My home point",
    anchorLive: "Wherever I am",
    anchorFixed: "A fixed place",
    anchorHint: {
      auto: "Your live position while the app is open, your home point otherwise. Best for most alerts.",
      home: "Always your saved home point. Works with the app closed.",
      live: "Only your live position — this alert goes quiet when the app has been closed a while.",
      fixed: "A place on the map, regardless of where you are.",
    },
    tapToPlace: "Tap the map to move the centre.",
    radius: "Radius",
    altMin: (unit: string) => `Lowest (${unit})`,
    altMax: (unit: string) => `Highest (${unit})`,
    altHint: "0–5,000 ft catches what's visibly overhead and ignores airliners at cruise.",
    altHintMetric: "0–1,500 m catches what's visibly overhead and ignores airliners at cruise.",
    leadTime: "Warn me",
    lead: "ahead",
    leadHint: "How far ahead of arrival to notify. Longer means less certain — it's a forecast.",
    cooldown: "Then stay quiet for",
    respectQuiet: "Respect quiet hours",
    dormant: "Not active",
    size: "Aircraft size",
    sizeAny: "Any aircraft",
    sizeLarge: "Airliners and bigger",
    sizeHeavy: "Wide-body only",
    sizeFour: "Four-engine only",
    sizeShort: { any: "", large: "airliners+", heavy: "wide-body", four_engine: "4-engine" } as Record<string, string>,
    preview: (matching: number, tracked: number) =>
      `${matching} of ${tracked} aircraft tracked right now fit this size and altitude.`,
    daylightOnly: "Only while the sun is up",
    daylightHint: "At night an aircraft is just lights in the sky. Worked out for where the zone is.",
    daylightShort: "daytime",
    daylightDefaultNote:
      "Daytime-only is on by default, including for alerts created before it existed. Turn it off per alert.",
    asleepUntil: (time: string) => `Asleep until sunrise, ${time}`,
    asleepNoSunrise: "Asleep — no sunrise in the next two days here",
    asleepQuiet: "Asleep — quiet hours",
    asleepQuietUntil: (time: string) => `Asleep — quiet hours until ${time}`,
    lastAlert: (when: string, week: number) => `Last alert ${when} · ${week} this week`,
    neverAlerted: "Hasn't alerted yet",
    justNow: "just now",
    recentTitle: "Recent alerts",
    closest: "closest",
    sendTest: "Send a test of this alert",
    testSent: (aircraft: string) => `Sent — using ${aircraft}, tracked right now.`,
    testSentSample: "Sent — using a sample aircraft, since nothing tracked right now fits.",
    testNoDevice: "No device accepted it. Turn on notifications in Settings first.",
    typesLink: "Aircraft types",
    typesHint: "Mute the models you've seen enough of.",
    delete: "Delete alert",
    enable: "Enable",
    disable: "Disable",
  },

  settings: {
    title: "Settings",
    homeTitle: "Home point",
    homeWhy:
      "Where alerts watch when the app is closed. A closed app can't report your position, so without this there are no background alerts.",
    noHome: "Not set — alerts only work while the app is open.",
    useCurrentLocation: "Use my location",
    locating: "Finding you…",
    clearHome: "Clear",
    locationFailed: "Couldn't get your location.",

    quietTitle: "Quiet hours",
    quietWhy: "Alerts that respect quiet hours stay silent between these times.",
    from: "From",
    to: "To",
    clearQuiet: "Clear quiet hours",

    notificationsTitle: "Notifications",
    pushWhy: "Get a notification before an aircraft enters one of your zones.",
    enableNotifications: "Enable notifications",
    disableNotifications: "Turn off",
    sendTest: "Send a test",
    pushOn: "Notifications are on for this device.",
    pushDenied:
      "Notifications are blocked for this site. You'll need to allow them in your browser or system settings.",
    pushUnsupported: "This browser doesn't support Web Push.",
    pushUnconfigured: "The server has no push keys configured yet.",
    iosInstallRequired:
      "On iPhone, notifications only work once this app is on your Home Screen.",
    iosSteps: [
      "Tap the Share button in Safari.",
      "Choose 'Add to Home Screen'.",
      "Open Flight Radar from the Home Screen, then come back here.",
    ],

    unitsTitle: "Units",
    unitsMetric: "Metric (m, km/h)",
    unitsAviation: "Aviation (ft, kt)",
    unitsHint: "Used on the map, in alerts and in notifications. Distances are always in km.",
    fatigueTitle: "Repeat types",
    fatigueWhy:
      "How many sightings before a type is offered up for muting. Higher means you'll be told about common aircraft for longer.",
    threshold: "Suggest muting after",
    thresholdUnit: "sightings",
    fatigueMode: "When the threshold is reached",
    fatigueSuggest: "Suggest it to me",
    fatigueEnforce: "Mute it automatically",
    fatigueModeHint:
      "Suggesting is the default: nothing gets silenced without you choosing it. Automatic mutes are labelled and can be undone in one tap.",

    accountTitle: "Account",
    signedInAs: "Signed in as",
    version: "Version",
  },

  search: {
    open: "Search",
    placeholder: "Callsign, registration or type",
    none: "No tracked aircraft match",
  },

  devices: {
    title: "Devices receiving alerts",
    unknown: "Unknown device",
    browser: "Browser",
    on: (browser: string, os: string) => `${browser} on ${os}`,
    thisDevice: "this device",
    added: (date: string) => `added ${date}`,
    lastDelivered: (date: string) => `last delivered ${date}`,
    neverDelivered: "nothing delivered yet",
    failures: (n: number) => `${n} failed`,
    remove: "Remove",
  },

  setup: {
    title: "Finish setting up alerts",
    install: "Add Flight Radar to your Home Screen",
    notifications: "Turn on notifications",
    home: "Give alerts somewhere to watch",
    homeAction: "Set a home point in Settings",
    rule: "Create an alert",
  },

  errors: {
    title: "Something went wrong",
    body: "The app hit an unexpected error. Reloading usually fixes it.",
    reload: "Reload",
    offline: "You're offline — the map will resume when the connection returns.",
  },

  units: { ft: "ft", kt: "kt", fpm: "ft/min" },
} as const;
